import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { message, Card, ConfigProvider, theme } from 'antd';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';

import { MODULE_IDS } from '../../utils/constants';
import { formatCurrency, formatQty } from '../../utils/formatters';

import { getMappings, createMapping, updateMapping, deleteMapping } from '../../api/mappings.api';
import { getUnits } from '../../api/units.api';

import SearchableSelect from '../../components/common/SearchableSelect';

/* ─────────────────────────────────────────────
   Light Theme Override Styles (Scoped to this page)
───────────────────────────────────────────── */
const LightThemeStyles = () => (
  <style>{`
    .light-mapping-page {
      /* Overriding global dark variables for this specific page */
      --bg: #f9fafb;
      --surface: #ffffff;
      --surface-2: #f3f4f6;
      --border: #e5e7eb;
      --border-hi: #d1d5db;
      --text-1: #111827;
      --text-2: #374151;
      --text-3: #6b7280;
      --accent: #f59e0b;
      --accent-dim: rgba(245, 158, 11, 0.15);
      --green: #10b981;
    }

    @keyframes rpFadeUp { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
    @keyframes rpSlideIn { from { transform:translateX(100%) } to { transform:translateX(0) } }
    @keyframes rpPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
    @keyframes rpSpin { to { transform:rotate(360deg) } }

    .rp-live-dot { animation: rpPulse 2s ease-in-out infinite; }
    .rp-drawer-enter { animation: rpSlideIn 0.28s cubic-bezier(0.4,0,0.2,1); }

    .rp-row:hover { background: var(--surface-2) !important; }
    .rp-act-edit:hover  { border-color:#3b82f6 !important; color:#3b82f6 !important; background:rgba(59,130,246,0.1) !important; }
    .rp-act-del:hover   { border-color:#ef4444 !important; color:#ef4444 !important; background:rgba(239,68,68,0.1) !important; }
    .rp-btn-add:hover   { background:#e08d00 !important; }
    .rp-btn-primary:hover:not(:disabled) { background:#e08d00 !important; }
    .rp-btn-ghost:hover { border-color:var(--border-hi) !important; color:var(--text-1) !important; background:var(--surface-2) !important; }
    .rp-form-input:focus  { border-color: var(--accent) !important; background: rgba(245,158,11,0.04) !important; }
    .rp-pg-btn-active { background: var(--accent-dim) !important; border-color: var(--accent) !important; color: var(--accent) !important; }
    .rp-pg-btn:hover:not(:disabled):not(.rp-pg-btn-active) { border-color: var(--border-hi) !important; color: var(--text-1) !important; }
    .rp-pg-btn:disabled { opacity: 0.35; cursor: not-allowed; }
    .rp-spinner { display:inline-block; width:12px; height:12px; border:2px solid rgba(0,0,0,0.25); border-top-color:var(--text-1); border-radius:50%; animation:rpSpin 0.6s linear infinite; }
    
    /* Native Select arrow styling */
    select.rp-form-input { appearance: none; background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2214%22%20height%3D%2214%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%236b7280%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E"); background-repeat: no-repeat; background-position: right 12px center; padding-right: 32px; }
    
    /* Card Styles Tuning */
    .light-mapping-page .ant-card {
      background: var(--surface);
      border-color: var(--border);
      border-radius: 8px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    }
    .light-mapping-page .ant-card-head {
      border-bottom-color: var(--border);
      padding: 16px 24px;
      min-height: auto;
    }
    .light-mapping-page .ant-card-body {
      padding: 24px;
    }
  `}</style>
);

/* ─────────────────────────────────────────────
   Inline styles
───────────────────────────────────────────── */
const S = {
  page: { fontFamily: "'Barlow', sans-serif", padding: '4px 0', animation: 'rpFadeUp 0.3s ease' },

  pageTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-1)', display: 'flex', alignItems: 'center', gap: 10, margin: 0 },
  titleDot: { width: 8, height: 8, background: 'var(--accent)', borderRadius: '50%', flexShrink: 0 },
  pageSubtitle: { fontSize: 12, color: 'var(--text-3)', marginTop: 4, letterSpacing: '0.3px', fontWeight: 'normal' },
  
  metaChip: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-3)', letterSpacing: '0.5px' },
  liveDot: { width: 5, height: 5, background: 'var(--green)', borderRadius: '50%' },

  btnAdd: { display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'var(--accent)', color: '#0a0c0f', border: 'none', borderRadius: 'var(--radius)', fontFamily: "'Barlow', sans-serif", fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.3px', transition: 'background var(--transition)' },

  filterBar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '12px 16px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', flexWrap: 'wrap' },
  filterItem: { flex: '1 1 240px', minWidth: 200 },

  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflowX: 'auto' },
  tableInner: { minWidth: 950 },
  tableHead: { display: 'grid', gridTemplateColumns: '40px minmax(140px, 1fr) minmax(140px, 1fr) 90px 100px 90px 100px 90px 80px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' },
  th: { padding: '10px 14px', fontSize: 10, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' },
  tableBody: { maxHeight: 500, overflowY: 'auto' },
  tableRow: { display: 'grid', gridTemplateColumns: '40px minmax(140px, 1fr) minmax(140px, 1fr) 90px 100px 90px 100px 90px 80px', borderBottom: '1px solid var(--border)', transition: 'background var(--transition)', cursor: 'default' },
  td: { padding: '12px 14px', fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', overflow: 'visible' },

  badge: { display: 'inline-flex', padding: '3px 8px', background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)', fontSize: 11, fontWeight: 600, borderRadius: 2, letterSpacing: '0.5px' },

  actBtn: { width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text-3)', transition: 'all var(--transition)', flexShrink: 0 },
  empty: { padding: '60px 24px', textAlign: 'center' },
  emptyText: { fontSize: 14, color: 'var(--text-3)', marginTop: 8 },

  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--surface-2)' },
  pgInfo: { fontSize: 12, color: 'var(--text-3)' },
  pgBtns: { display: 'flex', gap: 4 },
  pgBtn: { padding: '4px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text-2)', fontSize: 12, cursor: 'pointer', fontFamily: "'Barlow', sans-serif" },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400 },
  drawer: { position: 'fixed', right: 0, top: 0, bottom: 0, width: 480, background: 'var(--surface)', borderLeft: '1px solid var(--border)', zIndex: 401, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 15px rgba(0,0,0,0.05)' },
  drawerHeader: { padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0, background: 'var(--surface-2)' },
  drawerTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--text-1)', margin: 0 },
  drawerSubtitle: { fontSize: 11, color: 'var(--text-3)', marginTop: 3 },
  drawerClose: { width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text-3)', flexShrink: 0 },
  drawerBody: { flex: 1, overflowY: 'auto', padding: 24 },
  drawerFooter: { padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, flexShrink: 0, background: 'var(--surface-2)' },

  formItem: { marginBottom: 20 },
  formRow: { display: 'flex', gap: 16, marginBottom: 20 },
  formCol: { flex: 1 },
  formLabelRow: { display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 },
  formLabel: { fontSize: 11, fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--text-3)' },
  formRequired: { color: 'var(--accent)', fontSize: 13, lineHeight: 1 },
  formInput: { width: '100%', background: 'var(--bg)', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', padding: '9px 12px', fontFamily: "'Barlow', sans-serif", fontSize: 13, color: 'var(--text-1)', outline: 'none', transition: 'border-color var(--transition)' },
  formInputError: { borderColor: '#ef4444' },
  formHelp: { fontSize: 11, color: 'var(--text-3)', marginTop: 5, letterSpacing: '0.3px', lineHeight: 1.4 },
  formError: { fontSize: 11, color: '#ef4444', marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 },

  btnPrimary: { flex: 1, padding: '9px 16px', background: 'var(--accent)', color: '#0a0c0f', border: 'none', borderRadius: 'var(--radius)', fontFamily: "'Barlow', sans-serif", fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, transition: 'background var(--transition)' },
  btnGhost: { padding: '9px 16px', background: 'none', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', fontFamily: "'Barlow', sans-serif", fontSize: 13, fontWeight: 500, color: 'var(--text-2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all var(--transition)' },

  delPop: { position: 'absolute', right: 36, top: -20, background: 'var(--surface)', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', padding: 12, minWidth: 210, zIndex: 50, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' },
  delPopTitle: { fontSize: 12, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 },
  delPopText: { fontSize: 11, color: 'var(--text-3)', marginBottom: 10, lineHeight: 1.5 },
  delPopBtns: { display: 'flex', gap: 6 },
  btnCancelSm: { flex: 1, padding: '5px 8px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 11, color: 'var(--text-2)', cursor: 'pointer', fontFamily: "'Barlow', sans-serif" },
  btnDelSm: { flex: 1, padding: '5px 8px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', fontSize: 11, color: '#ef4444', cursor: 'pointer', fontWeight: 600, fontFamily: "'Barlow', sans-serif" },

  previewBox: { background: 'rgba(59, 130, 246, 0.05)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: 'var(--radius)', padding: '14px 16px', marginTop: 8, display: 'flex', gap: 12, alignItems: 'flex-start' },
  previewIcon: { color: '#3b82f6', marginTop: 2 },
  previewTitle: { fontSize: 12, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 },
  previewText: { fontSize: 13, color: 'var(--text-2)', lineHeight: 1.5 },
};

/* ─────────────────────────────────────────────
   SVG Icons
───────────────────────────────────────────── */
const Icon = {
  Plus: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  X: ({ size = 12 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  Edit: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  Trash: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>,
  Check: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>,
  Warning: () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
  Clear: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>,
  Layers: () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
  Info: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
};

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */
const DeletePopover = ({ itemName, onConfirm, onCancel }) => (
  <div style={S.delPop} onClick={e => e.stopPropagation()}>
    <div style={S.delPopTitle}>Delete Mapping?</div>
    <div style={S.delPopText}>Delete mapping for "{itemName}"?<br />This action cannot be undone.</div>
    <div style={S.delPopBtns}>
      <button style={S.btnCancelSm} onClick={onCancel}>Cancel</button>
      <button style={S.btnDelSm} onClick={onConfirm}>Delete</button>
    </div>
  </div>
);

const MappingRow = ({ record, index, canEdit, canDelete, onEdit, onDelete, isDeleting }) => {
  const [showDelPop, setShowDelPop] = useState(false);
  const delRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (delRef.current && !delRef.current.contains(e.target)) setShowDelPop(false); };
    if (showDelPop) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDelPop]);

  return (
    <div className="rp-row" style={S.tableRow}>
      <div style={{ ...S.td, color: 'var(--text-3)', fontSize: 12 }}>{index}</div>
      <div style={{ ...S.td, color: 'var(--text-1)', fontWeight: 500 }}>{record.productName || '—'}</div>
      <div style={{ ...S.td, color: 'var(--text-2)' }}>{record.rawName || '—'}</div>
      <div style={S.td}>{formatQty(record.productionQty)}</div>
      <div style={S.td}>{formatQty(record.consumptionQty)}</div>
      <div style={{ ...S.td, color: record.wastagePercent > 0 ? '#f59e0b' : 'inherit' }}>
        {record.wastagePercent}%
      </div>
      <div style={S.td}><span style={S.badge}>{record.unitDisplayName}</span></div>
      <div style={{ ...S.td, fontWeight: 500 }}>{formatCurrency(record.rate)}</div>
      
      <div style={{ ...S.td, gap: 6, position: 'relative' }}>
        {canEdit && (
          <button className="rp-act-edit" style={S.actBtn} onClick={() => onEdit(record)} title="Edit">
            <Icon.Edit />
          </button>
        )}
        {canDelete && (
          <div ref={delRef} style={{ position: 'relative' }}>
            <button className="rp-act-del" style={{ ...S.actBtn, ...(isDeleting ? { borderColor: '#ef4444', color: '#ef4444' } : {}) }} onClick={() => setShowDelPop(v => !v)} title="Delete">
              {isDeleting ? <span className="rp-spinner" style={{ borderColor: 'rgba(239,68,68,0.3)', borderTopColor: '#ef4444' }} /> : <Icon.Trash />}
            </button>
            {showDelPop && (
              <DeletePopover itemName={record.productName || 'Item'} onConfirm={() => { onDelete(record.mappingID); setShowDelPop(false); }} onCancel={() => setShowDelPop(false)} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
const MappingDrawer = ({ open, onClose, onSave, editing, loading, unitsData, plantId, control, handleSubmit, errors, watch, setValue, reset, selectedProductName, setSelectedProductName, selectedRawName, setSelectedRawName }) => {
  const drawerRef = useRef(null); // 🔴 NAYA: Drawer ko target karne ke liye ref
  const wProdQty = watch('productionQty');
  const wConsQty = watch('consumptionQty');
  const wUnitID = watch('unitID');

  useEffect(() => {
    if (open) {
      if (editing) {
        reset({
          readyProductID: editing.readyProductID, rawMaterialID: editing.rawMaterialID,
          productionQty: editing.productionQty, consumptionQty: editing.consumptionQty,
          wastagePercent: editing.wastagePercent || 0, unitID: editing.unitID, rate: editing.rate || 0
        });
        setSelectedProductName(editing.productName);
        setSelectedRawName(editing.rawName);
      } else {
        reset({ readyProductID: null, rawMaterialID: null, productionQty: 1, consumptionQty: 1, wastagePercent: 0, unitID: null, rate: 0 });
        setSelectedProductName('');
        setSelectedRawName('');
      }

      // 🔴 NAYA: Drawer khulte hi pehle input (Ready Product) par cursor lane ke liye
      setTimeout(() => {
        // SearchableSelect ke input ko target karne ki koshish
        const firstInput = drawerRef.current?.querySelector('.rp-form-input input, input'); 
        if (firstInput) firstInput.focus();
      }, 150);
    }
  }, [open, editing, reset, setSelectedProductName, setSelectedRawName]); 

  // 🔴 NAYA: Keyboard Navigation Logic (Enter-to-Next-Field)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (!drawerRef.current || !drawerRef.current.contains(e.target)) return;

      // Agar 'Save Mapping' button par Enter dabaya hai, toh form submit hone do
      if (document.activeElement.classList.contains('rp-btn-primary')) {
        return; 
      }

      // SearchableSelect ke andar khud ka ek "Enter" behavior hota hai option select karne ke liye.
      // Agar list khuli hui hai aur user Enter daba raha hai, toh pehle option select hone do.
      const isSearchableSelectInput = e.target.tagName === 'INPUT' && e.target.closest('[class*="control"]'); // React-select class pattern
      
      // Drawer ke andar ke focusable elements dhundho
      const focusable = drawerRef.current.querySelectorAll(
        'input:not([disabled]):not([readonly]):not([type="hidden"]), ' +
        'select:not([disabled]), ' +
        'button.rp-btn-primary:not([disabled])'
      );
      
      const elementsArr = Array.from(focusable);
      const activeIndex = elementsArr.indexOf(document.activeElement);

      if (activeIndex === -1) return;

      // Agar Searchable Select par hai aur dropdown khula hai, toh is event ko ignore karo (react-select handle karega)
      if (isSearchableSelectInput) {
        // Option select hone ke baad focus aage badhane ke liye chota timeout use karenge
        setTimeout(() => {
             const nextIndex = activeIndex + 1;
             if (nextIndex < elementsArr.length) {
               elementsArr[nextIndex].focus();
             }
        }, 150); // React-select ko state update karne ka time do
        return; 
      }

      // Default submit roko aur aage wale field par jump karo (Normal inputs ke liye)
      e.preventDefault(); 
      const nextIndex = activeIndex + 1;
      if (nextIndex < elementsArr.length) {
        const nextEl = elementsArr[nextIndex];
        nextEl.focus();
        if (nextEl.tagName === 'INPUT') {
          nextEl.select(); // Text select kar lo
        }
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const getPreviewText = () => {
    if (!wProdQty || !wConsQty) return null;
    const calc = (wConsQty / wProdQty) * 100;
    const selectedUnitObj = unitsData.find(u => u.unitID === wUnitID);
    const unitName = selectedUnitObj ? selectedUnitObj.consumptionUnit : 'units';
    const prodName = selectedProductName || 'Product';
    const rawName = selectedRawName || 'Raw Material';
    return `For 100 units of ${prodName}, you need ${calc.toFixed(4)} ${unitName} of ${rawName}`;
  };

  if (!open) return null;

  return (
    <>
      <div style={S.overlay} onClick={onClose} />
      {/* 🔴 NAYA: Yahan ref={drawerRef} add kiya hai */}
      <div style={S.drawer} className="rp-drawer-enter" ref={drawerRef}>
        <div style={S.drawerHeader}>
          <div>
            <div style={S.drawerTitle}>{editing ? 'Edit Mapping' : 'Add Mapping'}</div>
            <div style={S.drawerSubtitle}>{editing ? 'Modify the BOM below' : 'Define recipe/BOM for products'}</div>
          </div>
          <button style={S.drawerClose} className="rp-act-del" onClick={onClose}><Icon.X /></button>
        </div>

        <div style={S.drawerBody}>
          {/* Ready Product */}
          <div style={S.formItem}>
            <div style={S.formLabelRow}><span style={S.formLabel}>Ready Product</span><span style={S.formRequired}>*</span></div>
            <Controller
              name="readyProductID" control={control} rules={{ required: 'Ready Product is required' }}
              render={({ field }) => (
                <div className="rp-form-input" style={{ padding: 0, border: 'none', background: 'transparent', ...(errors.readyProductID ? { border: '1px solid #ef4444', borderRadius: 'var(--radius)' } : {}) }}>
                  <SearchableSelect 
                    {...field} apiUrl={`/plants/${plantId}/ready-products`} valueField="rpid" labelField="productName"
                    placeholder="Select Ready Product" disabled={!!editing}
                    onSelect={(obj) => { field.onChange(obj ? obj.rpid : null); setSelectedProductName(obj ? obj.productName : ''); }}
                  />
                </div>
              )}
            />
            {errors.readyProductID && <div style={S.formError}><Icon.Warning />{errors.readyProductID.message}</div>}
          </div>

          {/* Raw Material */}
          <div style={S.formItem}>
            <div style={S.formLabelRow}><span style={S.formLabel}>Raw Material</span><span style={S.formRequired}>*</span></div>
            <Controller
              name="rawMaterialID" control={control} rules={{ required: 'Raw Material is required' }}
              render={({ field }) => (
                <div className="rp-form-input" style={{ padding: 0, border: 'none', background: 'transparent', ...(errors.rawMaterialID ? { border: '1px solid #ef4444', borderRadius: 'var(--radius)' } : {}) }}>
                  <SearchableSelect 
                    {...field} apiUrl={`/plants/${plantId}/raw-materials`} valueField="rawID" labelField="rawName"
                    placeholder="Select Raw Material" disabled={!!editing} 
                    onSelect={(obj) => { 
                      field.onChange(obj ? obj.rawID : null); setSelectedRawName(obj ? obj.rawName : '');
                      if (obj && obj.unitID && !editing) setValue('unitID', obj.unitID);
                    }}
                  />
                </div>
              )}
            />
            {errors.rawMaterialID && <div style={S.formError}><Icon.Warning />{errors.rawMaterialID.message}</div>}
          </div>

          {/* Quantities */}
          <div style={S.formRow}>
            <div style={S.formCol}>
              <div style={S.formLabelRow}><span style={S.formLabel}>Production Qty (Pic)</span><span style={S.formRequired}>*</span></div>
              <Controller
                name="productionQty" control={control} rules={{ required: true, min: 0.0001 }}
                render={({ field }) => <input {...field} type="number" step="any" min="0.0001" className="rp-form-input" style={{ ...S.formInput, ...(errors.productionQty ? S.formInputError : {}) }} placeholder="1" />}
              />
              {errors.productionQty ? <div style={S.formError}><Icon.Warning />Required</div> : <div style={S.formHelp}>Units produced</div>}
            </div>
            
            <div style={S.formCol}>
              <div style={S.formLabelRow}><span style={S.formLabel}>Consumption Qty</span><span style={S.formRequired}>*</span></div>
              <Controller
                name="consumptionQty" control={control} rules={{ required: true, min: 0.0001 }}
                render={({ field }) => <input {...field} type="number" step="any" min="0.0001" className="rp-form-input" style={{ ...S.formInput, ...(errors.consumptionQty ? S.formInputError : {}) }} placeholder="1" />}
              />
              {errors.consumptionQty ? <div style={S.formError}><Icon.Warning />Required</div> : <div style={S.formHelp}>Raw material used</div>}
            </div>
          </div>

          {/* Wastage & Unit */}
          <div style={S.formRow}>
            <div style={S.formCol}>
              <div style={S.formLabelRow}><span style={S.formLabel}>Wastage %</span></div>
              <Controller
                name="wastagePercent" control={control} rules={{ min: {value: 0, message: 'Invalid'}, max: {value: 100, message: 'Max 100'} }}
                render={({ field }) => <input {...field} type="number" step="any" min="0" max="100" className="rp-form-input" style={{ ...S.formInput, ...(errors.wastagePercent ? S.formInputError : {}) }} placeholder="0" />}
              />
              {errors.wastagePercent && <div style={S.formError}><Icon.Warning />{errors.wastagePercent.message}</div>}
            </div>

            <div style={S.formCol}>
              <div style={S.formLabelRow}><span style={S.formLabel}>Unit</span><span style={S.formRequired}>*</span></div>
              <Controller
                name="unitID" control={control} rules={{ required: 'Required' }}
                render={({ field }) => (
                  <select {...field} className="rp-form-input" style={{ ...S.formInput, ...(errors.unitID ? S.formInputError : {}) }}>
                    <option value="" disabled>Select Unit...</option>
                    {unitsData.map(u => <option key={u.unitID} value={u.unitID}>{u.consumptionUnit}</option>)}
                  </select>
                )}
              />
              {errors.unitID && <div style={S.formError}><Icon.Warning />{errors.unitID.message}</div>}
            </div>
          </div>

          {/* Rate */}
          <div style={S.formItem}>
            <div style={S.formLabelRow}><span style={S.formLabel}>Rate (₹) per Unit</span></div>
            <Controller
              name="rate" control={control} rules={{ min: 0 }}
              render={({ field }) => <input {...field} type="number" step="any" min="0" className="rp-form-input" style={{ ...S.formInput, ...(errors.rate ? S.formInputError : {}) }} placeholder="0.00" />}
            />
          </div>

          {/* Recipe Preview */}
          {wProdQty > 0 && wConsQty > 0 && selectedProductName && selectedRawName && (
            <div style={S.previewBox}>
              <div style={S.previewIcon}><Icon.Info /></div>
              <div>
                <div style={S.previewTitle}>Recipe Preview</div>
                <div style={S.previewText}>{getPreviewText()}</div>
              </div>
            </div>
          )}
        </div>

        <div style={S.drawerFooter}>
          <button style={S.btnGhost} className="rp-btn-ghost" onClick={onClose}>Cancel</button>
          <button style={{ ...S.btnPrimary, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }} className="rp-btn-primary" onClick={handleSubmit(onSave)} disabled={loading}>
            {loading ? <><span className="rp-spinner" />Saving...</> : <><Icon.Check />Save Mapping</>}
          </button>
        </div>
      </div>
    </>
  );
};
/* ─────────────────────────────────────────────
   Main Page Component
───────────────────────────────────────────── */
const MappingPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  
  const [filterReadyProductId, setFilterReadyProductId] = useState(null);
  const [filterRawMaterialId, setFilterRawMaterialId] = useState(null);
  const [tableParams, setTableParams] = useState({ current: 1, pageSize: 8 });

  const [selectedProductName, setSelectedProductName] = useState('');
  const [selectedRawName, setSelectedRawName] = useState('');

  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();

  const { canAdd, canEdit, canDelete } = usePermissions(MODULE_IDS.MAPPING);

  const { control, handleSubmit, reset, watch, formState: { errors }, setValue } = useForm({
    defaultValues: { readyProductID: null, rawMaterialID: null, productionQty: 1, consumptionQty: 1, wastagePercent: 0, unitID: null, rate: 0 }
  });

  /* Queries */
  const { data, isLoading: isFetching } = useQuery({
    queryKey: ['mappings', plantId, filterReadyProductId, filterRawMaterialId, tableParams.current, tableParams.pageSize],
    queryFn: () => getMappings(plantId, { readyProductId: filterReadyProductId, rawMaterialId: filterRawMaterialId, page: tableParams.current, pageSize: tableParams.pageSize }),
    enabled: !!plantId,
  });

  const mappings = data?.items || [];
  const totalCount = data?.total || 0;

  const { data: unitsData = [] } = useQuery({
    queryKey: ['units', plantId], queryFn: () => getUnits(plantId), enabled: !!plantId,
  });

  /* Mutations */
  const createMutation = useMutation({
    mutationFn: (d) => createMapping(plantId, d),
    onSuccess: () => { message.success('Mapping created successfully!'); queryClient.invalidateQueries({ queryKey: ['mappings'] }); setDrawerOpen(false); },
    onError: (err) => message.error(err.message || 'Error creating mapping')
  });

  const updateMutation = useMutation({
    mutationFn: (d) => updateMapping(plantId, editingMapping.mappingID, d),
    onSuccess: () => { message.success('Mapping updated successfully!'); queryClient.invalidateQueries({ queryKey: ['mappings'] }); setDrawerOpen(false); },
    onError: (err) => message.error(err.message || 'Error updating mapping')
  });

  const deleteMutation = useMutation({
    mutationFn: (mappingId) => deleteMapping(plantId, mappingId),
    onSuccess: () => { message.success('Mapping deleted successfully!'); queryClient.invalidateQueries({ queryKey: ['mappings'] }); },
    onError: (err) => message.error(err.message || 'Cannot delete mapping.')
  });

  /* Handlers */
  const openDrawer = (record = null) => { setEditingMapping(record); setDrawerOpen(true); };
  
  const onSave = (formData) => {
    // Ensuring numbers
    const payload = { ...formData, productionQty: Number(formData.productionQty), consumptionQty: Number(formData.consumptionQty), wastagePercent: Number(formData.wastagePercent) || 0, rate: Number(formData.rate) || 0 };
    if (editingMapping) updateMutation.mutate(payload); else createMutation.mutate(payload);
  };

  const handleClearFilters = () => { setFilterReadyProductId(null); setFilterRawMaterialId(null); setTableParams({ current: 1, pageSize: 8 }); };

 
  const totalPages = Math.max(1, Math.ceil(totalCount / tableParams.pageSize));
  const getPageNums = () => {
    const p = tableParams.current, t = totalPages;
    if (t <= 5) return Array.from({ length: t }, (_, i) => i + 1);
    if (p <= 3) return [1, 2, 3, 4, 5];
    if (p >= t - 2) return [t - 4, t - 3, t - 2, t - 1, t];
    return [p - 2, p - 1, p, p + 1, p + 2];
  };

  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <LightThemeStyles />
      <div className="light-mapping-page" style={S.page}>

        <Card
          title={
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={S.pageTitle}>
                <span style={S.titleDot} />
                BOM / Mapping Master
              </div>
              <div style={S.pageSubtitle}>Define recipes and Bill of Materials for your products</div>
            </div>
          }
          extra={
            canAdd && (
              <button className="rp-btn-add" style={S.btnAdd} onClick={() => openDrawer(null)}>
                <Icon.Plus /> Add Mapping
              </button>
            )
          }
          styles={{ header: { padding: '20px 24px' } }}
        >

          {/* Toolbar (Filters) */}
          <div style={S.filterBar}>
            <div style={S.filterItem}>
              <SearchableSelect 
                apiUrl={`/plants/${plantId}/ready-products`} valueField="rpid" labelField="productName"
                placeholder="Filter by Ready Product" value={filterReadyProductId}
                onSelect={(val) => { setFilterReadyProductId(val ? val.rpid : null); setTableParams(prev => ({ ...prev, current: 1 })); }}
              />
            </div>
            <div style={S.filterItem}>
              <SearchableSelect 
                apiUrl={`/plants/${plantId}/raw-materials`} valueField="rawID" labelField="rawName"
                placeholder="Filter by Raw Material" value={filterRawMaterialId}
                onSelect={(val) => { setFilterRawMaterialId(val ? val.rawID : null); setTableParams(prev => ({ ...prev, current: 1 })); }}
              />
            </div>
            {(filterReadyProductId || filterRawMaterialId) && (
              <button style={S.btnGhost} onClick={handleClearFilters}><Icon.Clear /> Clear</button>
            )}
            
          <div style={{ ...S.metaChip, marginLeft: 'auto' }}>
              <span className="rp-live-dot" style={S.liveDot} />
              {totalCount} mapped record{totalCount !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Table */}
          <div style={S.tableWrap}>
            <div style={S.tableInner}>
              <div style={S.tableHead}>
                {['#', 'Ready Product', 'Raw Material', 'Prod Qty', 'Cons Qty', 'Wastage', 'Unit', 'Rate (₹)', 'Actions'].map(h => (
                  <div key={h} style={S.th}>{h}</div>
                ))}
              </div>
              
              <div style={S.tableBody}>
                {isFetching ? (
                  <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                    <span className="rp-spinner" style={{ width: 22, height: 22, borderWidth: 2, borderColor: 'var(--border-hi)', borderTopColor: 'var(--accent)' }} />
                  </div>
                ) : mappings.length === 0 ? (
                  <div style={S.empty}>
                    <div style={{ color: 'var(--text-3)', marginBottom: 8, display: 'flex', justifyContent: 'center' }}><Icon.Layers /></div>
                    <div style={S.emptyText}>{(filterReadyProductId || filterRawMaterialId) ? 'No mappings found matching filters.' : 'No BOM/Mappings defined yet. Click "Add Mapping" to get started.'}</div>
                  </div>
                ) : (
                  mappings.map((m, i) => (
                    <MappingRow
                      key={m.mappingID} record={m} index={(tableParams.current - 1) * tableParams.pageSize + i + 1}
                      canEdit={canEdit} canDelete={canDelete} onEdit={openDrawer}
                      onDelete={(id) => deleteMutation.mutate(id)}
                      isDeleting={deleteMutation.isPending && deleteMutation.variables === m.mappingID}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Pagination */}
            {totalCount > tableParams.pageSize && (
              <div style={S.pagination}>
                <div style={S.pgInfo}>Showing {(tableParams.current - 1) * tableParams.pageSize + 1}–{Math.min(tableParams.current * tableParams.pageSize, totalCount)} of {totalCount}</div>
                <div style={S.pgBtns}>
                  <button className="rp-pg-btn" style={S.pgBtn} disabled={tableParams.current === 1} onClick={() => setTableParams(p => ({ ...p, current: p.current - 1 }))}>‹ Prev</button>
                  {getPageNums().map(n => (
                    <button key={n} className={`rp-pg-btn${n === tableParams.current ? ' rp-pg-btn-active' : ''}`} style={S.pgBtn} onClick={() => setTableParams(p => ({ ...p, current: n }))}>{n}</button>
                  ))}
                  <button className="rp-pg-btn" style={S.pgBtn} disabled={tableParams.current === totalPages} onClick={() => setTableParams(p => ({ ...p, current: p.current + 1 }))}>Next ›</button>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Drawer */}
        <MappingDrawer
          open={drawerOpen} onClose={() => setDrawerOpen(false)} onSave={onSave} editing={editingMapping} loading={isMutating}
          unitsData={unitsData} plantId={plantId} control={control} handleSubmit={handleSubmit} errors={errors} watch={watch} setValue={setValue} reset={reset}
          selectedProductName={selectedProductName} setSelectedProductName={setSelectedProductName} selectedRawName={selectedRawName} setSelectedRawName={setSelectedRawName}
        />
      </div>
    </ConfigProvider>
  );
};

export default MappingPage;